import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import classNames from 'classnames';

import ScoreWidget from 'components/ScoreWidget/ScoreWidget';
import { GeneralWebSocketSubscriptionTypes, isTabletOrMobileDevice } from 'constants/app';
import { marketGraphsBranding } from 'constants/branding';
import EventsUpdatesInjection from 'injections/EventsUpdatesInjection';
import { getMarketGraphIsSmallScreenView, getOperator } from 'redux/modules/appConfigs/selectors';
import { getLoggedInStatusState } from 'redux/modules/auth/selectors';
import {
  getMarketDefinitionInPlayStatus,
  getMarketDefinitionTurnInPlayEnabled
} from 'redux/modules/marketDefinitions/selectors';
import { fetchGraphMarket } from 'redux/modules/marketGraph';
import { getGraphMarketInfo, getGraphMarketLoading } from 'redux/modules/marketGraph/selectors';
import { splitMarketNameIntoParticipants } from 'utils/market';
import { getIconNameForMobileNavigation } from 'utils/navigation';

import MarketGraphDropdown from '../MarketGraphDropdown/MarketGraphDropdown';
import MarketGraphHeaderSkeleton from '../MarketGraphHeaderSkeleton/MarketGraphHeaderSkeleton';
import MarketGraphClosePageBtn from '../MarketGraphMobileClosePageBtn/MarketGraphClosePageBtn';

import styles from './MarketGraphHeader.module.scss';

export default function MarketGraphHeader() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { marketId } = useParams();

  const isLoggedIn = useSelector(getLoggedInStatusState);
  const isSmallScreenView = useSelector(getMarketGraphIsSmallScreenView);
  const market = useSelector(getGraphMarketInfo);
  const marketLoading = useSelector(getGraphMarketLoading);
  const isMarketInPlay = useSelector(getMarketDefinitionInPlayStatus(market?.marketId || ''));
  const turnInPlayEnabled = useSelector(getMarketDefinitionTurnInPlayEnabled(market?.marketId || ''));
  const operator = useSelector(getOperator);

  const eventName = market?.eventName || '';
  const eventParticipants = splitMarketNameIntoParticipants(eventName);

  const sectionName = market?.orderedNameSegments.join(' - ');
  const isMobile = isTabletOrMobileDevice || isSmallScreenView;

  const isLoading = !market || (marketLoading && market?.marketId !== marketId);

  useEffect(() => {
    if (marketId && market?.marketId !== marketId) {
      dispatch(fetchGraphMarket({ marketId }));
    }
  }, [marketId]);

  return (
    <>
      <div
        className={classNames(styles.header, marketGraphsBranding.HEADER, {
          [styles.header__mobile]: isMobile,
          [styles.loading]: isLoading,
          [styles.loading__mobile]: isLoading && isMobile
        })}
      >
        {isLoading ? (
          <MarketGraphHeaderSkeleton />
        ) : (
          <>
            <EventsUpdatesInjection
              eventIds={[market.eventId]}
              subscriptionType={GeneralWebSocketSubscriptionTypes.eventInfoUpdates}
            />
            <div className={styles.header__left}>
              <i
                className={classNames(
                  styles.sportIcon,
                  'biab_br-sport-icon',
                  getIconNameForMobileNavigation(market.eventType.id, operator)
                )}
              />
              {!market.outright && (
                <div className={classNames({ 'biab_inplay-sports-container': isMarketInPlay })}>
                  <ScoreWidget
                    isInPlay={isMarketInPlay}
                    eventId={market.eventId}
                    sportId={market.eventType.id}
                    marketStartTime={market.startTime}
                    isDateOnly={!turnInPlayEnabled}
                    className={classNames(styles.score, {
                      [styles.score__noBorder]: isMarketInPlay,
                      [styles.score__mobile]: isTabletOrMobileDevice || isSmallScreenView
                    })}
                  />
                </div>
              )}
              {eventParticipants.length === 2 ? (
                <div className={styles.participants}>
                  <div>{eventParticipants[0]}</div>
                  <div>{eventParticipants[1]}</div>
                </div>
              ) : (
                <div className={styles.eventName}>{eventName}</div>
              )}
              {isTabletOrMobileDevice && <MarketGraphClosePageBtn />}
            </div>
            {isLoggedIn && !isTabletOrMobileDevice && (
              <div className={styles.header__right}>
                <div className={styles.section}>
                  <span>{t('marketGraph.labels.showDataFor')}</span>
                  <span className={styles.sectionName}>{sectionName}</span>
                  <span>:</span>
                </div>
                <MarketGraphDropdown isFancy={market.fancy} />
              </div>
            )}
          </>
        )}
      </div>
      {!isLoading && isLoggedIn && isTabletOrMobileDevice && (
        <div
          className={classNames(styles.header, styles.header__sub, marketGraphsBranding.HEADER, {
            [styles.header__mobile]: isMobile,
            [styles.loading]: isLoading,
            [styles.loading__mobile]: isLoading && isMobile
          })}
        >
          <div className={styles.header__right}>
            <div className={styles.section}>
              <span>{t('marketGraph.labels.showDataFor')}</span>
              <span className={styles.sectionName}>{sectionName}</span>
              <span>:</span>
            </div>
            <MarketGraphDropdown isFancy={market.fancy} />
          </div>
        </div>
      )}
    </>
  );
}
